import React from "react";

import { renderToStaticMarkup } from "react-dom/server";

import TooltipLines from "@/src/components/tooltip/TooltipLines";
import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";

import {
  transactionEmailColumn,
  transactionNameColumn,
  transactionOfferColumn,
  transactionPaymentColumn,
} from "./transactionColumns";
import { getSubscriptionPayment } from "../../../../utils/data/Payment";
import DataTableColumnHeader from "../../../dataTable/DataTableColumnHeader";
import { dateColumn } from "../../../dataTable/dataTableColumns";

import type { TFunction } from "i18next";

import type { UnsubscriberRow } from "./unsubscriberColumns.types";
import type {
  Reason,
  UnsubscriberTransaction,
} from "../../../../api/StatsAPI.types";
import type { DataTableColumnDef } from "../../../dataTable/dataTable.types";

interface UnsubscriberRowOptions {
  t: TFunction;
  lang: string;
  translateReasons: Reason[];
  projectReasons: Reason[] | undefined;
}

interface UnsubscriberColumnsOptions {
  t: TFunction;
  projectSlug: string | undefined;
  userAbility: boolean;
  offerAbility: boolean;
  hasPaypalProvider: boolean;
}

/** Traduction de chaque raison : la langue de l'éditeur, sinon la première. */
function buildReasonLocales(
  translateReasons: Reason[],
  lang: string,
): Record<string, string> {
  const locales: Record<string, string> = {};
  translateReasons.forEach(({ name, translations }) => {
    const [firstKey] = Object.keys(translations);
    locales[name] =
      translations[lang] ?? (firstKey ? translations[firstKey] : "") ?? "";
  });
  return locales;
}

function getReasons(
  transaction: UnsubscriberTransaction,
  locales: Record<string, string>,
  { t, lang, projectReasons }: UnsubscriberRowOptions,
): string[] {
  if (transaction.unsubscription_method === "auto") {
    return [t("unsubscribe_reasons.payment_failed")];
  }

  return Object.keys(transaction.unsubscribe_reasons)
    .filter((reason) => transaction.unsubscribe_reasons[reason])
    .map((reason) => {
      if (reason === "other") {
        return t("unsubscribe_reasons.other", {
          reason: transaction.description_reason,
        });
      }

      return (
        locales[reason] ??
        projectReasons?.find(({ name }) => name === reason)?.translations[
          lang
        ] ??
        t(`unsubscribe_reasons.${reason}`)
      );
    });
}

export function unsubscriberRows(
  transactions: UnsubscriberTransaction[],
  options: UnsubscriberRowOptions,
): UnsubscriberRow[] {
  const locales = buildReasonLocales(options.translateReasons, options.lang);

  return transactions.map((transaction) => ({
    uuid: transaction.subscription_uuid,
    user_uuid: transaction.user_uuid,
    offer_uuid: transaction.offer_uuid,
    email: transaction.user.email,
    name: `${transaction.user.first_name} ${transaction.user.last_name}`,
    payment: getSubscriptionPayment(transaction),
    retailer_name: transaction.retailer_name,
    offer_label: transaction.offer.label,
    subscription_date: transaction.subscription_date,
    unsubscription_date: transaction.unsubscription_date,
    subscription_end_date: transaction.subscription_end_date,
    reasons: getReasons(transaction, locales, options),
  }));
}

export { getTransactionUserHref as getUnsubscriberHref } from "./transactionColumns";

export function unsubscriberColumns({
  t,
  projectSlug,
  userAbility,
  offerAbility,
  hasPaypalProvider,
}: UnsubscriberColumnsOptions): DataTableColumnDef<UnsubscriberRow>[] {
  return [
    transactionEmailColumn<UnsubscriberRow>(t, projectSlug, userAbility, {
      minWidth: 150,
    }),
    transactionNameColumn<UnsubscriberRow>(t, { minWidth: 150 }),
    {
      ...transactionPaymentColumn<UnsubscriberRow>(t, hasPaypalProvider),
      enableHiding: true,
    },
    transactionOfferColumn<UnsubscriberRow>(
      t("offer"),
      projectSlug,
      offerAbility,
      { minWidth: 150 },
    ),
    {
      ...dateColumn<UnsubscriberRow>(
        "subscription_date",
        t("subscription_date"),
        { width: "12%", minWidth: 140 },
      ),
      enableHiding: true,
    },
    dateColumn<UnsubscriberRow>(
      "unsubscription_date",
      t("unsubscription_date"),
      { width: "13%", minWidth: 160 },
    ),
    {
      ...dateColumn<UnsubscriberRow>(
        "subscription_end_date",
        t("subscription_end_date"),
        { width: "15%", minWidth: 185 },
      ),
      enableHiding: true,
    },
    {
      accessorKey: "reasons",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("reasons")} />
      ),
      enableSorting: false,
      meta: { searchable: true, label: t("reasons"), minWidth: 150 },
      cell: ({ row }) => {
        const { reasons } = row.original;
        if (reasons.length === 0) return null;

        return (
          <span
            className="truncate"
            data-tooltip-id={APP_TOOLTIP_ID}
            data-tooltip-html={renderToStaticMarkup(
              <TooltipLines lines={reasons} />,
            )}
          >
            {reasons.join(", ")}
          </span>
        );
      },
    },
  ];
}
